import dayjs from '@/dayjsSetup';
import useLanguageStore from '@/stores/useLanguageStore';
import { convertToArabicNumbers } from './numberHelpers';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

dayjs.extend(relativeTime);

/**
 * Formats a date string using dayjs with optional localization to Arabic numerals.
 * Returns '---' for invalid or empty dates.
 *
 * IMPORTANT: This function treats the input as UTC and converts to Iraq timezone (Asia/Baghdad, UTC+3).
 * Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 * Limitation: Without dayjs timezone plugin, this uses a fixed +3 hour offset.
 *
 * @param {string | undefined} date - The ISO date string from backend (assumed UTC)
 * @param {string | undefined} format - The format string for dayjs (e.g., 'YYYY-MM-DD')
 * @returns {string} Formatted date string in Iraq timezone, or '---' for invalid dates
 */
export const formatDateByDayjs = (date?: string, format?: string) => {
  if (!date) return '---';

  // Parse as UTC explicitly to ensure deterministic behavior
  const dayjsDate = dayjs.utc(date);
  if (!dayjsDate.isValid()) return '---';

  // Convert to Iraq timezone (UTC+3) by adding 3 hours
  const iraqTime = dayjsDate.add(3, 'hour');

  const language = useLanguageStore.getState().language;
  const formattedDate = iraqTime.format(format);

  return language === 'en' ? formattedDate : convertToArabicNumbers(formattedDate);
};

/**
 * Returns a human-readable relative time if the date is within specified hours,
 * otherwise returns a formatted date using formatDateByDayjs.
 *
 * IMPORTANT: This function treats the input as UTC and converts to Iraq timezone for calculations.
 * Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 *
 * @param {string | undefined} date - The ISO date string from backend (assumed UTC)
 * @param {string} format - Format for full date (e.g., 'YYYY-MM-DD HH:mm')
 * @param {number | undefined} limitHours - Hour limit for relative time display
 * @returns {string}
 */
export const formatDateOrRelative = (
  date?: string,
  format = 'YYYY-MM-DD HH:mm',
  limitHours?: number,
) => {
  if (!date) return '---';

  // Parse as UTC explicitly
  const d = dayjs.utc(date);
  if (!d.isValid()) return '---';

  // Get current time in UTC for accurate diff calculation
  const now = dayjs.utc();
  const diffInHours = now.diff(d, 'hour');
  const language = useLanguageStore.getState().language;

  // Convert to Iraq timezone for relative time display
  const result = language === 'en' ? d.fromNow() : d.locale('ar').fromNow();

  if (!limitHours || (limitHours && diffInHours < limitHours)) {
    return result;
  }

  return formatDateByDayjs(date, format);
};

/**
 * Checks if a date is close to expiration (within 24 hours from now).
 *
 * IMPORTANT: This function treats the input as UTC and compares against current UTC time.
 * This ensures consistent behavior regardless of user's local timezone.
 *
 * @param {string | undefined} expirationDate - The expiration date string (assumed UTC)
 * @returns {boolean} True if the date is within 24 hours, false otherwise
 */
export const isCloseToExpiration = (expirationDate?: string): boolean => {
  if (!expirationDate) return false;

  // Parse as UTC explicitly
  const expDate = dayjs.utc(expirationDate);
  if (!expDate.isValid()) return false;

  // Compare with current UTC time
  const now = dayjs.utc();
  const hoursUntilExpiration = expDate.diff(now, 'hour');

  // Consider close to expiration if less than 24 hours remain
  return hoursUntilExpiration >= 0 && hoursUntilExpiration <= 24;
};

/**
 * Converts UTC date string to Iraq timezone and formats as time in HH:mm format.
 * Converts to Arabic numerals if the current language is Arabic.
 *
 * IMPORTANT: This function treats the input as UTC and converts to Iraq timezone (Asia/Baghdad, UTC+3).
 * Note: Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 * Limitation: Without dayjs timezone plugin, this uses a fixed +3 hour offset.
 *
 * @param {string | undefined} date - The ISO date string (e.g., '2024-10-15T14:13:47+00:00')
 * @returns {string} Formatted time string in Iraq timezone (e.g., '17:13'), or '---' for invalid dates
 */
export const formatTimeInIraqTimezone = (date?: string): string => {
  if (!date) return '---';

  // Parse as UTC explicitly to ensure deterministic behavior
  const dayjsDate = dayjs.utc(date);
  if (!dayjsDate.isValid()) return '---';

  // Convert to Iraq timezone (UTC+3) by adding 3 hours
  // Iraq has not observed DST since 2008, so UTC+3 is constant
  const iraqTime = dayjsDate.add(3, 'hour');

  const language = useLanguageStore.getState().language;
  const formattedTime = iraqTime.format('HH:mm');

  return language === 'en' ? formattedTime : convertToArabicNumbers(formattedTime);
};

/**
 * Converts UTC date string to Iraq timezone and formats as full datetime.
 * Converts to Arabic numerals if the current language is Arabic.
 *
 * IMPORTANT: This function treats the input as UTC and converts to Iraq timezone (Asia/Baghdad, UTC+3).
 * Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 * Limitation: Without dayjs timezone plugin, this uses a fixed +3 hour offset.
 *
 * @param {string | undefined} date - The ISO date string (e.g., '2024-10-15T14:13:47+00:00')
 * @param {string} format - The format string (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} Formatted datetime string in Iraq timezone, or '---' for invalid dates
 */
export const formatDateTimeInIraqTimezone = (
  date?: string,
  format: string = 'YYYY-MM-DD HH:mm',
): string => {
  if (!date) return '---';

  // Parse as UTC explicitly to ensure deterministic behavior
  const dayjsDate = dayjs.utc(date);
  if (!dayjsDate.isValid()) return '---';

  // Convert to Iraq timezone (UTC+3) by adding 3 hours
  const iraqTime = dayjsDate.add(3, 'hour');

  const language = useLanguageStore.getState().language;
  const formattedDateTime = iraqTime.format(format);

  return language === 'en' ? formattedDateTime : convertToArabicNumbers(formattedDateTime);
};

/**
 * Converts UTC date string to Iraq timezone and formats as date only (no time).
 * Converts to Arabic numerals if the current language is Arabic.
 *
 * IMPORTANT: This function treats the input as UTC and converts to Iraq timezone (Asia/Baghdad, UTC+3).
 * Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 * Limitation: Without dayjs timezone plugin, this uses a fixed +3 hour offset.
 *
 * @param {string | undefined} date - The ISO date string (e.g., '2024-10-15T14:13:47+00:00')
 * @param {string} format - The format string (default: 'YYYY-MM-DD')
 * @returns {string} Formatted date string in Iraq timezone, or '---' for invalid dates
 */
export const formatDateInIraqTimezone = (date?: string, format: string = 'YYYY-MM-DD'): string => {
  if (!date) return '---';

  // Parse as UTC explicitly to ensure deterministic behavior
  const dayjsDate = dayjs.utc(date);
  if (!dayjsDate.isValid()) return '---';

  // Convert to Iraq timezone (UTC+3) by adding 3 hours
  const iraqTime = dayjsDate.add(3, 'hour');

  const language = useLanguageStore.getState().language;
  const formattedDate = iraqTime.format(format);

  return language === 'en' ? formattedDate : convertToArabicNumbers(formattedDate);
};

/**
 * Gets the current time in Iraq timezone.
 *
 * IMPORTANT: Returns current UTC time converted to Iraq timezone (Asia/Baghdad, UTC+3).
 * Iraq does not observe DST since 2008, so UTC+3 is used year-round.
 * Limitation: Without dayjs timezone plugin, this uses a fixed +3 hour offset.
 *
 * @returns {dayjs.Dayjs} Current time in Iraq timezone
 */
export const getCurrentIraqTime = (): dayjs.Dayjs => {
  // Get current UTC time and convert to Iraq timezone
  return dayjs.utc().add(3, 'hour');
};

/**
 * Converts a dayjs date object to UTC ISO string for sending to backend.
 * Use this when sending DatePicker selections or any date to the API.
 *
 * @param {dayjs.Dayjs | undefined} date - The dayjs date object
 * @returns {string | undefined} ISO string in UTC format, or undefined
 */
export const toUTCString = (date?: dayjs.Dayjs): string | undefined => {
  if (!date) return undefined;
  // Treat the input as Iraq time and convert back to UTC by subtracting 3 hours
  return date.subtract(3, 'hour').utc().toISOString();
};

/**
 * Formats a bare pickup-slot time ("HH:mm" or "HH:mm:ss", already Iraq-local —
 * the wire format of slot window_from/window_to) as 12-hour "h:mm A"
 * ("3:00 PM" / "٣:٠٠ م"). Mirrors the mobile app's AppDateUtils.formatSlotTime12
 * so both clients render identical slot times. Unparseable input passes
 * through untouched.
 */
export const formatSlotTime12 = (raw?: string | null): string => {
  const s = (raw ?? '').trim();
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return s;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return s;
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const language = useLanguageStore.getState().language;
  const marker = hour >= 12 ? (language === 'en' ? 'PM' : 'م') : (language === 'en' ? 'AM' : 'ص');
  const text = `${h12}:${String(minute).padStart(2, '0')} ${marker}`;
  return language === 'en' ? text : convertToArabicNumbers(text);
};

/**
 * Joins a slot window as "3:00 PM – 6:00 PM" (spaced en-dash, 12-hour).
 * Returns '' when either side is missing so callers keep their has-window
 * guards unchanged.
 */
export const formatSlotWindow12 = (from?: string | null, to?: string | null): string => {
  const f = (from ?? '').trim();
  const t = (to ?? '').trim();
  if (!f || !t) return '';
  return `${formatSlotTime12(f)} – ${formatSlotTime12(t)}`;
};
